import { PERF_THRESHOLDS } from "./perf";

/**
 * Product metrics (docs/metrics.md): the catalogue the server's rollup jobs fill, the admin
 * Metrics API serves and the dashboard draws. One entry per metric; the server's rollups are
 * keyed by these ids (a test checks every metric has one, and nothing else is written).
 *
 * Grains: `hour` buckets are UTC hours, `day` buckets are Pacific-time days (Sam's day). Every
 * value is an aggregate: no account or character id is kept past the 30-day raw event log.
 */

export type MetricSection = "funnel" | "players" | "gameplay" | "performance";
export type MetricGrain = "hour" | "day";
/** A metric split by something: one row per value of it, `""` when not split. */
export type MetricDim = "zone" | "device" | "event" | "quest" | "cause" | "item" | "kind";

export interface MetricDef {
  id: string;
  section: MetricSection;
  label: string;
  unit:
    | "players"
    | "accounts"
    | "count"
    | "minutes"
    | "ms"
    | "fps"
    | "per-minute"
    | "%"
    | "MB"
    | "bytes/s";
  grains: MetricGrain[];
  dim?: MetricDim;
  /** Which way is better (the dashboard's delta colour); `none`: neither. */
  good: "up" | "down" | "none";
  /** The "needs a look" line from PERF_THRESHOLDS, when Health has one. */
  threshold?: number;
  /** Where it comes from, for docs/metrics.md and the dashboard's info line. */
  source: string;
}

const HD: MetricGrain[] = ["hour", "day"];
const D: MetricGrain[] = ["day"];

export const METRICS = [
  // ---- the account funnel: counts of account timestamps on that day (players only)
  {
    id: "funnel.created",
    section: "funnel",
    label: "Accounts created",
    unit: "accounts",
    grains: D,
    good: "up",
    source: "accounts.created_at",
  },
  {
    id: "funnel.verified",
    section: "funnel",
    label: "Emails verified",
    unit: "accounts",
    grains: D,
    good: "up",
    source: "accounts.email_verified_at",
  },
  {
    id: "funnel.reserved",
    section: "funnel",
    label: "Reservations paid",
    unit: "accounts",
    grains: D,
    good: "up",
    source: "billing_reservations.paid_at",
  },
  {
    id: "funnel.admitted",
    section: "funnel",
    label: "Admitted",
    unit: "accounts",
    grains: D,
    good: "up",
    source: "subscriptions.admitted_at",
  },
  {
    id: "funnel.first_session",
    section: "funnel",
    label: "First sessions",
    unit: "accounts",
    grains: D,
    good: "up",
    source: "accounts.first_played_at",
  },
  // ---- players
  {
    id: "players.ccu_peak",
    section: "players",
    label: "Peak concurrent players",
    unit: "players",
    grains: HD,
    good: "up",
    source: "server.perf players (each minute's end)",
  },
  {
    id: "players.ccu_mean",
    section: "players",
    label: "Average concurrent players",
    unit: "players",
    grains: HD,
    good: "up",
    source: "server.perf players",
  },
  {
    id: "players.zone_minutes",
    section: "players",
    label: "Player-minutes by zone",
    unit: "minutes",
    grains: D,
    dim: "zone",
    good: "none",
    source: "server.perf zones[].players",
  },
  {
    id: "players.active",
    section: "players",
    label: "Active players",
    unit: "players",
    grains: D,
    good: "up",
    source: "distinct characters with conn.attached",
  },
  {
    id: "players.active_accounts",
    section: "players",
    label: "Active accounts",
    unit: "accounts",
    grains: D,
    good: "up",
    source: "distinct accounts with conn.attached",
  },
  {
    id: "players.hours",
    section: "players",
    label: "Player-hours",
    unit: "count",
    grains: D,
    good: "up",
    source: "server.perf players, summed per minute",
  },
  {
    id: "sessions.count",
    section: "players",
    label: "Sessions",
    unit: "count",
    grains: D,
    good: "up",
    source: "conn.detached",
  },
  {
    id: "sessions.minutes_p50",
    section: "players",
    label: "Session length (median)",
    unit: "minutes",
    grains: D,
    good: "up",
    source: "conn.detached seconds",
  },
  {
    id: "sessions.minutes_p90",
    section: "players",
    label: "Session length (p90)",
    unit: "minutes",
    grains: D,
    good: "up",
    source: "conn.detached seconds",
  },
  {
    id: "sessions.bounces",
    section: "players",
    label: "Bounced sessions (under 2 min)",
    unit: "count",
    grains: D,
    good: "down",
    source: "conn.detached seconds",
  },
  // ---- gameplay
  {
    id: "activity.count",
    section: "gameplay",
    label: "Activities",
    unit: "count",
    grains: D,
    dim: "event",
    good: "up",
    source: "gather, fish, craft, build, turnin, quest.complete, mob.kill events",
  },
  {
    id: "quests.accepted",
    section: "gameplay",
    label: "Quests accepted",
    unit: "count",
    grains: D,
    dim: "quest",
    good: "up",
    source: "quest.accept",
  },
  {
    id: "quests.completed",
    section: "gameplay",
    label: "Quests completed",
    unit: "count",
    grains: D,
    dim: "quest",
    good: "up",
    source: "quest.complete",
  },
  {
    id: "economy.in",
    section: "gameplay",
    label: "Items in, by way",
    unit: "count",
    grains: D,
    dim: "kind",
    good: "none",
    source: "gather / fish catches / crafts / turn-ins / caches / corpse recoveries (actions)",
  },
  {
    id: "economy.out",
    section: "gameplay",
    label: "Items out, by way",
    unit: "count",
    grains: D,
    dim: "kind",
    good: "none",
    source: "item.drop / museum.donate / shrine.offering / npc.gift (items)",
  },
  {
    id: "economy.out_item",
    section: "gameplay",
    label: "Items out, by item",
    unit: "count",
    grains: D,
    dim: "item",
    good: "none",
    source: "item.drop / museum.donate / shrine.offering / npc.gift",
  },
  {
    id: "economy.fish_item",
    section: "gameplay",
    label: "Fish caught, by item",
    unit: "count",
    grains: D,
    dim: "item",
    good: "none",
    source: "fish (a catch)",
  },
  {
    id: "deaths.count",
    section: "gameplay",
    label: "Deaths",
    unit: "count",
    grains: D,
    dim: "cause",
    good: "down",
    source: "player.death by",
  },
  // ---- performance: the server's minutes, the clients' sessions (by device)
  {
    id: "perf.tick_p95",
    section: "performance",
    label: "Tick p95",
    unit: "ms",
    grains: HD,
    good: "down",
    threshold: PERF_THRESHOLDS.tickP95Ms,
    source: "server.perf tickP95, p95 over the minutes",
  },
  {
    id: "perf.tick_worst",
    section: "performance",
    label: "Worst minute's tick p95",
    unit: "ms",
    grains: HD,
    good: "down",
    threshold: PERF_THRESHOLDS.tickP95Ms,
    source: "server.perf tickP95, max over the minutes",
  },
  {
    id: "perf.loop_p99",
    section: "performance",
    label: "Event-loop delay p99",
    unit: "ms",
    grains: HD,
    good: "down",
    threshold: PERF_THRESHOLDS.loopP99Ms,
    source: "server.perf loopP99, p95 over the minutes",
  },
  {
    id: "perf.cpu",
    section: "performance",
    label: "Server CPU",
    unit: "%",
    grains: HD,
    good: "down",
    threshold: PERF_THRESHOLDS.cpuPct,
    source: "server.perf cpuPct, mean",
  },
  {
    id: "perf.steal",
    section: "performance",
    label: "CPU steal",
    unit: "%",
    grains: HD,
    good: "down",
    threshold: PERF_THRESHOLDS.stealPct,
    source: "server.perf stealPct, mean",
  },
  {
    id: "perf.rss",
    section: "performance",
    label: "Server memory (peak)",
    unit: "MB",
    grains: HD,
    good: "down",
    threshold: PERF_THRESHOLDS.rssMb,
    source: "server.perf rssMb, max",
  },
  {
    id: "perf.bytes",
    section: "performance",
    label: "Outbound traffic",
    unit: "bytes/s",
    grains: HD,
    good: "none",
    source: "server.perf bytesPerSec, mean",
  },
  {
    id: "client.fps_p50",
    section: "performance",
    label: "Client fps (median session)",
    unit: "fps",
    grains: HD,
    dim: "device",
    good: "up",
    source: "client.session fpsMedian",
  },
  {
    id: "client.fps_p10",
    section: "performance",
    label: "Client fps, slow sessions (p10)",
    unit: "fps",
    grains: HD,
    dim: "device",
    good: "up",
    source: "client.session fpsP10, p10 over sessions",
  },
  {
    id: "client.rtt_p95",
    section: "performance",
    label: "Round trip p95",
    unit: "ms",
    grains: HD,
    dim: "device",
    good: "down",
    threshold: PERF_THRESHOLDS.rttP95Ms,
    source: "client.session rttP95, p95 over sessions",
  },
  {
    id: "client.snap_gap_p95",
    section: "performance",
    label: "Snapshot gap p95",
    unit: "ms",
    grains: HD,
    dim: "device",
    good: "down",
    threshold: PERF_THRESHOLDS.snapGapP95Ms,
    source: "client.session snapGapP95, p95 over sessions",
  },
  {
    id: "client.corrections",
    section: "performance",
    label: "Snap-backs per minute",
    unit: "per-minute",
    grains: HD,
    dim: "device",
    good: "down",
    threshold: PERF_THRESHOLDS.correctionsPerMinute,
    source: "client.session corrections over seconds played",
  },
  {
    id: "client.load_p50",
    section: "performance",
    label: "Load time (median)",
    unit: "ms",
    grains: D,
    dim: "device",
    good: "down",
    source: "client.session loadMs",
  },
] as const satisfies readonly MetricDef[];

export type MetricId = (typeof METRICS)[number]["id"];

/** The account funnel's steps, in order (metrics_cohort `step`). */
export const COHORT_STEPS = [
  "created",
  "verified",
  "reserved",
  "admitted",
  "first_session",
  "d1",
  "d7",
] as const;
export type CohortStep = (typeof COHORT_STEPS)[number];

/** The time zone of a `day` bucket. */
export const METRICS_TZ = "America/Los_Angeles";
